import React from 'react'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux'

import {
  selectChatBox,
  updateValue,
  setChannelValue,
  addAlert,
  clearUniqueAlerts
} from '../../../../../slices/chatBoxSlice'
import { useCanSend } from '../../../../../providers/CanSendProvider'
import { useOwner } from '../../../../../providers/OwnerProvider'
import useRowStyles from '../ExtraInputRow/styles'
import useStyles from './styles'

const EmailInput = ({ disabled }) => {
  const dispatch = useAppDispatch()
  const { currentID, values } = useAppSelector(selectChatBox)

  const { canSend, setCanSend } = useCanSend()
  const { owner } = useOwner()

  const subject = React.useMemo(() => values.subject ?? '', [values.subject])
  const rootRef = React.useRef(null)
  const [emails, allEmails, loaded] = React.useMemo(() => {
    return [values.emails ?? [], values.allEmails ?? {}, Boolean(currentID)]
  }, [currentID, values])

  const rowClasses = useRowStyles()
  const classes = useStyles()

  React.useEffect(() => {
    if (!loaded) {
      return
    }

    if (Object.values(allEmails).length === 0) {
      dispatch(
        addAlert({
          message: (
            <>
              <span className='bold'>{owner?.name}</span> has no email
              addresses, please add one.
            </>
          ),
          unique: 'email-none'
        })
      )
      dispatch(updateValue('emails', []))
      dispatch(updateValue('emailSet', false))
      return
    }

    dispatch(clearUniqueAlerts('email-none'))

    if (emails.length !== 0) {
      return
    }

    if (!values.emailSet) {
      const id = Object.values(allEmails)[0].channel.id
      dispatch(setChannelValue({ isPhone: false, id }))
    }
  }, [loaded, allEmails, emails.length, values.emailSet, dispatch, owner])

  const getDisabled = React.useCallback(
    id => {
      const ch = allEmails[id]?.channel

      if (!ch) {
        return true
      }

      return !Boolean(ch.compliance && !ch.deleted_at && ch.validated)
    },
    [allEmails]
  )

  // Update canSend
  React.useEffect(() => {
    const hasEmails = emails.length !== 0
    const allEmailsValid = emails.every(id => !getDisabled(id))

    setCanSend(hasEmails && allEmailsValid)

    if (!allEmailsValid) {
      const singular = emails.length === 1
      const message = `${
        singular
          ? 'Selected email address'
          : 'One or more of the selected email addresses'
      } has not been validated or has been opted-out by the user.`
      dispatch(
        addAlert({
          message,
          unique: 'email-invalid'
        })
      )
    } else {
      dispatch(clearUniqueAlerts('email-invalid'))
    }
  }, [setCanSend, emails, getDisabled, dispatch])

  // Update stored subject
  const handleSubjectChange = React.useCallback(
    e => {
      const val = e.target.value
      dispatch(updateValue('subject', val))
    },
    [dispatch]
  )

  return (
    <div className={classes.root}>
      <div className={rowClasses.root}>
        <div className={rowClasses.label}>To</div>

        <div ref={rootRef} className={rowClasses.row}>
          {emails.length === 0 ? (
            <div
              onClick={e => e.stopPropagation()}
              className={clsx(rowClasses.button, rowClasses.dropdown)}
            >
              {Object.keys(allEmails).length === 0
                ? 'No channels'
                : 'Select email'}
            </div>
          ) : (
            emails.map((id, i) => (
              <div
                key={id}
                onClick={e => e.stopPropagation()}
                className={clsx(rowClasses.button, rowClasses.dropdown)}
              >
                {allEmails[id]?.channel.value ?? ''}
              </div>
            ))
          )}
        </div>
      </div>

      <div onClick={e => e.stopPropagation()} className={rowClasses.root}>
        <div className={rowClasses.label} style={{ marginBottom: '0' }}>
          Subject
        </div>

        <input
          placeholder='Enter a subject...'
          value={subject}
          onChange={handleSubjectChange}
          className={classes.input}
          disabled={disabled || !canSend}
        />
      </div>
    </div>
  )
}

export default EmailInput
